import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import { getStatement, formatPeriodLabel, formatUsd, downloadStatementPdf, sendStatementWhatsapp } from '../lib/statements';

const NAVY = '#0A2540';
const MUTED = '#6B7280';
const LINE = '#EFF1F4';
const SOFT = '#F9FAFB';

function Section({ title, children, right }) {
  return (
    <section className="mb-8">
      <div className="flex items-end justify-between mb-3">
        <h2 className="text-xs font-semibold tracking-wider uppercase text-[#6B7280]">{title}</h2>
        {right}
      </div>
      <div className="bg-white border border-[#EFF1F4] rounded-xl">{children}</div>
    </section>
  );
}

// tone: 'gain' (entra, verde +) | 'loss' (sale, rojo −) | 'neutral' (movimiento interno, gris)
// muted: fila sin movimientos ($0) -> se atenua para que resalte lo que si paso.
function Row({ label, value, sub, tone, muted }) {
  const tones = { gain: { c: GAIN, s: '+' }, loss: { c: LOSS, s: '−' }, neutral: { c: '#475467', s: '' } };
  const t = !muted && tone ? tones[tone] : null;
  const valueColor = muted ? '#9CA3AF' : (t ? t.c : '#181D27');
  return (
    <div className="flex items-start justify-between px-5 py-3 border-b border-[#EFF1F4] last:border-b-0">
      <div className="flex items-start gap-2">
        {t && <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: t.c }} />}
        <div>
          <div className={`text-sm ${muted ? 'text-[#9CA3AF]' : 'text-[#181D27]'}`}>{label}</div>
          {sub && <div className="text-xs text-[#6B7280] mt-0.5">{sub}</div>}
        </div>
      </div>
      <div className="text-sm font-semibold whitespace-nowrap" style={{ color: valueColor }}>{t?.s || ''}{value}</div>
    </div>
  );
}

const GAIN = '#047857';
const LOSS = '#B91C1C';

// Nombres de empresa/fondo para mostrar como descripcion (estilo Schwab).
const SYMBOL_NAMES = {
  AAPL: 'Apple Inc', MSFT: 'Microsoft Corp', NVDA: 'NVIDIA Corp', TSLA: 'Tesla Inc',
  PLTR: 'Palantir Technologies', HIMS: 'Hims & Hers Health', DUOL: 'Duolingo Inc',
  AMD: 'Advanced Micro Devices', 'BRK.B': 'Berkshire Hathaway B',
  VOO: 'Vanguard S&P 500 ETF', QQQ: 'Invesco QQQ Trust', BND: 'Vanguard Total Bond ETF',
  GLD: 'SPDR Gold Shares', SCHD: 'Schwab US Dividend ETF', SOXX: 'iShares Semiconductor ETF',
  ARKK: 'ARK Innovation ETF', VWO: 'Vanguard Emerging Markets ETF', VYM: 'Vanguard High Dividend ETF',
  VNQ: 'Vanguard Real Estate ETF', O: 'Realty Income Corp', KO: 'Coca-Cola Co',
};
const symbolName = (sym) => SYMBOL_NAMES[sym] || sym;

// Fila de posicion estilo extracto de corretaje (Schwab): cantidad, costo
// promedio, precio actual, costo base, valor de mercado y ganancia/perdida.
function PositionRow({ p }) {
  const qty = Number(p.qty || 0);
  const avgEntry = Number(p.avgEntry || 0);
  const marketValue = Number(p.marketValue || 0);
  const curPrice = qty > 0 ? marketValue / qty : 0;
  const costBasis = qty * avgEntry;
  const hasCost = costBasis > 0;
  const gainUsd = marketValue - costBasis;
  const gainPct = hasCost ? (gainUsd / costBasis) * 100 : 0;
  const up = gainUsd >= 0;
  const gainColor = up ? GAIN : LOSS;

  return (
    <div className="px-5 py-3 border-b border-[#EFF1F4] last:border-b-0">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold text-[#181D27]">{p.symbol}</div>
          <div className="text-xs text-[#6B7280]">{symbolName(p.symbol)}</div>
        </div>
        <div className="text-sm font-semibold text-[#181D27]">{formatUsd(marketValue)}</div>
      </div>
      <div className="text-xs text-[#6B7280] mt-0.5">
        {qty.toFixed(4)} unidades · peso {((p.weight || 0) * 100).toFixed(1)}%
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
        <div className="flex justify-between">
          <span className="text-[#6B7280]">Costo promedio</span>
          <span className="text-[#181D27]">{hasCost ? formatUsd(avgEntry) : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#6B7280]">Precio actual</span>
          <span className="text-[#181D27]">{formatUsd(curPrice)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#6B7280]">Costo total</span>
          <span className="text-[#181D27]">{hasCost ? formatUsd(costBasis) : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#6B7280]">Ganancia/perdida</span>
          {hasCost ? (
            <span style={{ color: gainColor }} className="font-medium">
              {up ? '+' : ''}{formatUsd(gainUsd)} ({up ? '+' : ''}{gainPct.toFixed(2)}%)
            </span>
          ) : <span className="text-[#181D27]">—</span>}
        </div>
      </div>
    </div>
  );
}

export default function Statement() {
  const { period } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Mundo-consciente: el extracto vive en personas (/dashboard/statements) y en
  // empresas (/business/extractos); los links de volver respetan cada mundo.
  const listPath = location.pathname.startsWith('/business') ? '/business/extractos' : '/dashboard/statements';
  const snap = useSnapshot(state);
  // El WhatsApp se arma solo con DIGITOS: el area_code guardado puede venir como
  // "+57", "57" o con espacios, y el Bridge rechaza (o malforma) un numero con "+"
  // en medio -> el envio daba ok=true pero no llegaba. Normalizamos a puros digitos.
  const userPhone = (() => {
    const pn = snap.user?.phonenumber;
    if (!pn) return '';
    const soloDigitos = (x) => String(x || '').replace(/\D/g, '');
    const code = soloDigitos(pn.nationality?.area_code) || '57'; // Colombia por defecto
    const num = soloDigitos(pn.number);
    return num ? `${code}${num}` : '';
  })();
  const [searchParams] = useSearchParams();
  const shouldRefresh = searchParams.get('refresh') === '1';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfErr, setPdfErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [waLoading, setWaLoading] = useState(false);
  const [waMsg, setWaMsg] = useState(null);

  // Envia el audio del extracto al WhatsApp del cliente (audio + enlace de respaldo).
  const handleWhatsapp = async () => {
    if (!userPhone) { setWaMsg({ ok: false, text: 'No tienes un teléfono registrado en tu perfil.' }); return; }
    setWaLoading(true);
    setWaMsg(null);
    try {
      await sendStatementWhatsapp(period, userPhone);
      setWaMsg({ ok: true, text: 'Listo, te enviamos tu extracto narrado a WhatsApp.' });
    } catch (e) {
      setWaMsg({ ok: false, text: e.response?.data?.message || 'No pudimos enviarlo. Intenta de nuevo.' });
    } finally {
      setWaLoading(false);
      setTimeout(() => setWaMsg(null), 6000);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setErr(null);
    try {
      const d = await getStatement(period, { refresh: true });
      setData(d);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDownload = async () => {
    setPdfLoading(true);
    setPdfErr(null);
    try {
      await downloadStatementPdf(period);
    } catch (e) {
      setPdfErr('No pudimos generar el PDF. Intentalo de nuevo en un momento.');
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    getStatement(period, { refresh: shouldRefresh })
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e.response?.data?.message || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period, shouldRefresh]);

  if (loading) {
    return (
      <div className="px-4 sm:px-10 py-8">
        <div className="text-sm text-[#6B7280]">Cargando tu extracto...</div>
        <div className="text-xs text-[#9CA3AF] mt-2">
          Si es la primera vez que lo abres, el Oraculo lo esta preparando con tu resumen narrado — puede tardar hasta un minuto.
        </div>
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="px-4 sm:px-10 py-8">
        <div className="text-sm text-red-600 mb-3">No pudimos cargar tu extracto: {err || 'sin datos'}</div>
        <button onClick={() => navigate(listPath)} className="text-sm underline text-[#0A2540]">Ver listado</button>
      </div>
    );
  }

  const deltaPositive = (data.deltaUsd || 0) >= 0;
  const deltaLabel = `${deltaPositive ? '+' : ''}${formatUsd(data.deltaUsd)} vs mes anterior · ${deltaPositive ? '+' : ''}${(data.deltaPct || 0).toFixed(2)}%`;
  const netWorth = data.totalNetWorthUsd != null
    ? data.totalNetWorthUsd
    : (data.closingBalanceUsd || 0) + (data.investments?.totalValueUsd || 0);
  const netWorthDeltaLabel = (data.netWorthDeltaUsd != null)
    ? `${data.netWorthDeltaUsd >= 0 ? '+' : ''}${formatUsd(data.netWorthDeltaUsd)} vs mes anterior${data.netWorthDeltaPct != null ? ` · ${data.netWorthDeltaPct >= 0 ? '+' : ''}${data.netWorthDeltaPct.toFixed(2)}%` : ''}`
    : null;
  const crossBorderTotal = data.crossBorder?.totalAmountUsd || 0;
  const crossBorderFee = data.crossBorder?.totalFeeBaseUsd || 0;
  const crossBorderIva = data.crossBorder?.totalIvaUsd || 0;
  const positions = data.investments?.positions || [];
  const movements = data.crossBorder?.movements || [];
  const invMarketValue = positions.reduce((s, p) => s + Number(p.marketValue || 0), 0);
  const invCostBasis = positions.reduce((s, p) => s + Number(p.qty || 0) * Number(p.avgEntry || 0), 0);
  const invGainUsd = invMarketValue - invCostBasis;
  const invGainPct = invCostBasis > 0 ? (invGainUsd / invCostBasis) * 100 : 0;
  const invUp = invGainUsd >= 0;

  // Saldos multi-moneda (pesos, dolares, inversiones, Kamino)
  const bal = data.balances || {};
  const rate = Number(bal.copUsdRate || 0);
  const fmtCop = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-CO') + ' COP';
  const copCop = Number(bal.copCop || 0);
  const usdAvail = Number(bal.usdAvailableUsd != null ? bal.usdAvailableUsd : data.closingBalanceUsd || 0);
  const kaminoUsd = Number(bal.kaminoUsd || 0);
  const netWorthCop = Number(bal.totalNetWorthCop || (rate ? netWorth * rate : 0));

  // Distribucion de activos (incluye pesos + dolares + inversiones + Kamino)
  const allocTotal = netWorth > 0 ? netWorth : (usdAvail + invMarketValue);
  const alloc = [
    { label: 'Pesos (COP)', value: Number(bal.copInUsd || 0), color: '#7C9CE0', cop: copCop },
    { label: 'Dolares disponibles', value: usdAvail, color: '#2A59C6' },
    { label: 'Acciones y ETFs', value: invMarketValue, color: '#0A2540' },
    { label: 'Rendimientos (Kamino)', value: kaminoUsd, color: '#16A34A' },
  ].filter((a) => a.value > 0);

  // Categorias de movimientos del mes
  const cat = data.movementsByCategory || {};
  const sumc = data.summary || {};
  const moveCats = [
    { key: 'recargas', label: 'Recargas (PSE)', total: sumc.totalRechargedUsd, tone: 'gain' },
    { key: 'conversiones', label: 'Conversiones COP / USD', total: sumc.totalConvertedUsd, tone: 'neutral' },
    { key: 'retirosLocal', label: 'Retiros a banco en Colombia', total: sumc.totalLocalWithdrawUsd, tone: 'loss' },
    { key: 'enviosInternacional', label: 'Envios internacionales', total: sumc.totalCrossBorderUsd, tone: 'loss' },
    { key: 'transferencias', label: 'Transferencias a usuarios FlowexPay', total: sumc.totalTransfersUsd, tone: 'neutral' },
    { key: 'kamino', label: 'Movimientos de rendimiento (Kamino)', total: sumc.totalKaminoUsd, tone: 'gain' },
  ];
  // Resumen del flujo del mes (para los chips de arriba): entro / salio / invertido.
  const flow = {
    entro: Number(sumc.totalRechargedUsd || 0) + Number(sumc.totalKaminoUsd || 0),
    salio: Number(sumc.totalLocalWithdrawUsd || 0) + Number(sumc.totalCrossBorderUsd || 0) + Number(sumc.totalFeesBaseUsd || 0) + Number(sumc.totalIvaUsd || 0),
    invertido: Number(sumc.totalInvestedUsd || 0),
  };

  // RENTABILIDAD (del mes + acumulada). Usa los campos nuevos del backend;
  // si el statement es viejo (cacheado antes), cae a calcular la acumulada desde posiciones.
  const inv = data.investments || {};
  const hasInvest = invMarketValue > 0 || Number(inv.totalValueUsd || 0) > 0;
  const cumUsd = inv.cumulativeReturnUsd != null ? Number(inv.cumulativeReturnUsd) : invGainUsd;
  const cumPct = inv.cumulativeReturnPct != null ? Number(inv.cumulativeReturnPct) : invGainPct;
  const hasMonthly = !!inv.hasMonthlyReturn && inv.monthlyReturnPct != null;
  const monUsd = Number(inv.monthlyReturnUsd || 0);
  const monPct = Number(inv.monthlyReturnPct || 0);
  const RET_GREEN = '#16A34A', RET_RED = '#DC2626';
  const signPct = (n) => (n >= 0 ? '+' : '') + Number(n || 0).toFixed(2) + '%';
  // Fecha de corte de la rentabilidad (cuando se genero el extracto = primer dia habil del mes)
  const asOfDate = data.generatedAt ? new Date(data.generatedAt) : null;
  const asOfLabel = asOfDate
    ? asOfDate.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  // Fila de rentabilidad con recuadros separados para Dolares y Pesos
  const retRow = ({ label, sub, usd, pct }) => {
    const color = usd >= 0 ? RET_GREEN : RET_RED;
    const usdStr = (usd >= 0 ? '+' : '-') + formatUsd(Math.abs(usd));
    const copStr = rate
      ? (usd >= 0 ? '+' : '-') + '$' + Math.round(Math.abs(usd * rate)).toLocaleString('es-CO')
      : '—';
    return (
      <div className="py-3 border-b border-[#EFF1F4] last:border-b-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-[#181D27]">{label}{sub ? <span className="text-xs text-[#6B7280]"> {sub}</span> : null}</span>
          <span className="text-sm font-semibold" style={{ color }}>{signPct(pct)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-[#EFF1F4] bg-[#F9FAFB] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-0.5">Dolares (USD)</div>
            <div className="text-sm font-semibold" style={{ color }}>{usdStr}</div>
          </div>
          <div className="rounded-lg border border-[#EFF1F4] bg-[#F9FAFB] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-0.5">Pesos (COP)</div>
            <div className="text-sm font-semibold" style={{ color }}>{copStr}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-10 py-6 max-w-3xl mx-auto" style={{ background: SOFT }}>
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#EFF1F4]">
        <Link to={listPath} className="text-xs text-[#6B7280] hover:underline">← Todos los extractos</Link>
        <div className="flex items-center gap-3">
          <div className="text-sm font-medium text-[#181D27]">{formatPeriodLabel(period)}</div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-xs px-3 py-1.5 rounded-md border border-[#EFF1F4] hover:bg-[#F9FAFB] text-[#0A2540] disabled:opacity-60"
          >
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </button>
          <button
            onClick={handleDownload}
            disabled={pdfLoading}
            className="text-xs px-3 py-1.5 rounded-md border border-[#EFF1F4] hover:bg-[#F9FAFB] text-[#0A2540] disabled:opacity-60"
          >
            {pdfLoading ? 'Generando...' : 'Descargar PDF'}
          </button>
          <button
            onClick={handleWhatsapp}
            disabled={waLoading}
            className="text-xs px-3 py-1.5 rounded-md text-white font-medium disabled:opacity-60"
            style={{ background: '#25D366' }}
          >
            {waLoading ? 'Enviando...' : 'Enviar a WhatsApp'}
          </button>
        </div>
      </div>
      {pdfErr && <div className="text-xs text-red-600 mb-3">{pdfErr}</div>}
      {waMsg && <div className={`text-xs mb-3 ${waMsg.ok ? 'text-[#166534]' : 'text-red-600'}`}>{waMsg.text}</div>}

      {/* HERO */}
      <div className="rounded-xl px-6 py-7 mb-8 text-white" style={{ background: NAVY }}>
        <div className="text-xs uppercase tracking-wider opacity-80 mb-2">Tu mes en FlowexPay</div>
        <div className="text-xs opacity-70 mb-1">Patrimonio total</div>
        <div className="text-4xl font-bold mb-1">{formatUsd(netWorth)} USD</div>
        {netWorthCop > 0 && <div className="text-sm opacity-80 mb-2">equivale a {fmtCop(netWorthCop)}</div>}
        {netWorthDeltaLabel && <div className="text-xs opacity-70 mt-1">{netWorthDeltaLabel}</div>}
      </div>

      {/* RENTABILIDAD (del mes + acumulada, con recuadros USD/COP y fecha de corte) */}
      {hasInvest && (
        <div className="rounded-xl bg-white border border-[#EFF1F4] px-6 py-5 mb-8">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-wider text-[#6B7280]">Rentabilidad de tus inversiones</span>
            {asOfLabel && <span className="text-xs text-[#6B7280]">al {asOfLabel}</span>}
          </div>
          {hasMonthly && retRow({ label: 'Este mes', usd: monUsd, pct: monPct })}
          {retRow({ label: 'Acumulada', sub: '(desde tu primera compra)', usd: cumUsd, pct: cumPct })}
          {!hasMonthly && (
            <div className="text-xs text-[#6B7280] mt-2">La rentabilidad del mes aparece el primer dia habil del mes, cuando se emite tu extracto.</div>
          )}
        </div>
      )}

      {/* TUS SALDOS AL CIERRE (multi-moneda) */}
      {alloc.length > 0 && allocTotal > 0 && (
        <Section title="Tus saldos al cierre">
          <div className="px-5 py-4">
            <div className="flex h-3 rounded-full overflow-hidden mb-4">
              {alloc.map((a) => (
                <div key={a.label} style={{ width: `${(a.value / allocTotal) * 100}%`, background: a.color }} />
              ))}
            </div>
            {alloc.map((a) => (
              <div key={a.label} className="flex items-center justify-between py-1.5 border-b border-[#EFF1F4] last:border-b-0">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: a.color }} />
                  <span className="text-sm text-[#181D27]">{a.label}</span>
                </div>
                <div className="text-sm text-[#181D27] text-right">
                  {a.cop != null ? <>{fmtCop(a.cop)} <span className="text-xs text-[#6B7280]">(≈ {formatUsd(a.value)})</span></> : formatUsd(a.value)}
                  <span className="text-xs text-[#6B7280]"> · {((a.value / allocTotal) * 100).toFixed(1)}%</span>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm font-semibold text-[#181D27]">Patrimonio total</span>
              <span className="text-sm font-semibold text-[#181D27]">{formatUsd(allocTotal)} · 100%</span>
            </div>
          </div>
        </Section>
      )}

      {/* MOVIMIENTOS DEL MES (todas las operaciones FlowexPay) */}
      <Section title="Movimientos del mes">
        <div className="flex gap-2 px-5 py-4 border-b border-[#EFF1F4]">
          <div className="flex-1 rounded-lg px-3 py-2.5 text-center" style={{ backgroundColor: '#ECFDF3' }}>
            <div className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: GAIN }}>Entró</div>
            <div className="text-sm font-bold mt-0.5" style={{ color: GAIN }}>{formatUsd(flow.entro)}</div>
          </div>
          <div className="flex-1 rounded-lg px-3 py-2.5 text-center" style={{ backgroundColor: '#FEF3F2' }}>
            <div className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: LOSS }}>Salió</div>
            <div className="text-sm font-bold mt-0.5" style={{ color: LOSS }}>{formatUsd(flow.salio)}</div>
          </div>
          <div className="flex-1 rounded-lg px-3 py-2.5 text-center" style={{ backgroundColor: '#F2F4F7' }}>
            <div className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#475467' }}>Invertiste</div>
            <div className="text-sm font-bold mt-0.5" style={{ color: '#475467' }}>{formatUsd(flow.invertido)}</div>
          </div>
        </div>
        {moveCats.map((m) => {
          const count = cat[m.key]?.count || 0;
          return (
            <Row
              key={m.key}
              label={m.label}
              value={formatUsd(m.total)}
              sub={count > 0 ? `${count} operacion(es)` : 'Sin movimientos'}
              tone={m.tone}
              muted={count === 0}
            />
          );
        })}
        <Row label="Inversiones (acciones/ETFs)" value={formatUsd(data.summary.totalInvestedUsd)} sub={`${data.investments?.tradesCount || 0} operacion(es)`} tone="neutral" muted={!(data.investments?.tradesCount > 0)} />
        <Row label="Comisiones FlowexPay" value={formatUsd(data.summary.totalFeesBaseUsd)} sub="Comisiones que pagaste (sin IVA)" tone="loss" muted={!(Number(data.summary.totalFeesBaseUsd) > 0)} />
        <Row label="IVA pagado (todas las operaciones)" value={formatUsd(data.summary.totalIvaUsd)} sub="19% sobre comisiones — informativo" tone="loss" muted={!(Number(data.summary.totalIvaUsd) > 0)} />
      </Section>

      {/* DETALLE DE MOVIMIENTOS POR CATEGORIA (solo las que tienen operaciones) */}
      {moveCats.filter((m) => (cat[m.key]?.movements || []).length > 0).map((m) => {
        const c = cat[m.key];
        return (
          <Section key={m.key} title={`Detalle: ${m.label}`}>
            {c.movements.map((mv, i) => (
              <Row
                key={i}
                label={mv.description}
                sub={`${new Date(mv.date).toLocaleDateString('es-CO')}${mv.feeBase ? ` · Comision ${formatUsd(mv.feeBase)} + IVA ${formatUsd(mv.iva)}` : ''}${mv.siigoInvoiceId ? ' · Factura DIAN' : ''}`}
                value={formatUsd(mv.amountUsd)}
              />
            ))}
            <div className="px-5 py-3 bg-[#F9FAFB] border-t border-[#EFF1F4] rounded-b-xl">
              <div className="flex justify-between text-xs text-[#6B7280]"><span>Total ({c.count})</span><span>{formatUsd(c.totalUsd)}</span></div>
              {(c.feeBase > 0 || c.iva > 0) && (
                <div className="flex justify-between text-xs text-[#6B7280] mt-1"><span>Comisiones + IVA</span><span>{formatUsd(c.feeBase)} + {formatUsd(c.iva)}</span></div>
              )}
            </div>
          </Section>
        );
      })}

      {/* INVERSIONES */}
      <Section title="Inversiones">
        {positions.length === 0 ? (
          <div className="px-5 py-6 text-sm text-[#6B7280]">
            Aun no tienes inversiones activas.{' '}
            <Link to="/dashboard/investments" className="underline text-[#0A2540]">Conoce tu portafolio</Link>.
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-[#EFF1F4]">
              <div className="flex justify-between">
                <div className="text-sm text-[#181D27]">Valor de mercado</div>
                <div className="text-sm font-semibold text-[#181D27]">{formatUsd(invMarketValue)}</div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 mt-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-[#6B7280]">Costo total</span>
                  <span className="text-[#181D27]">{invCostBasis > 0 ? formatUsd(invCostBasis) : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6B7280]">Ganancia/perdida</span>
                  {invCostBasis > 0 ? (
                    <span style={{ color: invUp ? GAIN : LOSS }} className="font-medium">
                      {invUp ? '+' : ''}{formatUsd(invGainUsd)} ({invUp ? '+' : ''}{invGainPct.toFixed(2)}%)
                    </span>
                  ) : <span className="text-[#181D27]">—</span>}
                </div>
              </div>
            </div>
            {data.investments.portfolioType && (
              <div className="px-5 py-2 text-xs text-[#6B7280] border-b border-[#EFF1F4]">
                Portafolio: <span className="capitalize text-[#181D27]">{data.investments.portfolioType}</span>
              </div>
            )}
            {positions.map((p) => (
              <PositionRow key={p.symbol} p={p} />
            ))}
            <div className="px-5 py-3 text-xs text-[#6B7280] bg-[#F9FAFB] rounded-b-xl">
              La ganancia/perdida es no realizada (sobre precio de mercado actual) y no constituye un evento fiscal hasta que vendas. Costo promedio segun tus compras en Alpaca.
            </div>
          </>
        )}
      </Section>

      {/* RESUMEN FISCAL */}
      <Section title="Resumen fiscal informativo">
        <Row label="IVA pagado en el mes" value={formatUsd(data.fiscalSummary.ivaPaidUsd)} sub="19% sobre comisiones (Art. 420 ET)" />
        <Row label="Retencion en la fuente acumulada" value={formatUsd(data.fiscalSummary.retencionFuenteUsd)} sub="Acumulado del mes" />
        <div className="px-5 py-3 text-xs text-[#6B7280] bg-[#F9FAFB] rounded-b-xl">
          {data.fiscalSummary.note}
        </div>
      </Section>

      {/* ORACULO INSIGHT + AUDIO */}
      {data.oraculoInsight && (
        <Section title="El Oraculo dice">
          <div className="px-5 py-4 text-sm text-[#181D27] leading-relaxed">{data.oraculoInsight}</div>
          {data.oraculoAudioUrl && (
            <div className="px-5 pb-4">
              <audio
                controls
                preload="none"
                src={`${data.oraculoAudioUrl}?v=${data.generatedAt ? new Date(data.generatedAt).getTime() : ''}`}
                className="w-full h-10 rounded-lg"
                style={{ filter: 'hue-rotate(200deg) saturate(0.6)' }}
              >
                Tu navegador no soporta audio.
              </audio>
              <p className="text-[10px] text-[#9CA3AF] mt-1">
                Tu resumen mensual narrado por el Oraculo
              </p>
            </div>
          )}
        </Section>
      )}

      {/* FOOTER LEGAL */}
      <div className="text-xs text-[#9CA3AF] leading-relaxed mt-10 pt-6 border-t border-[#EFF1F4]">
        Este extracto es informativo y refleja tus movimientos en FlowexPay (Loaded Colombia S.A.S.).
        Las facturas electronicas con timbre DIAN se emiten via Siigo y son la fuente fiscal oficial.
        Tu certificado anual de retenciones en la fuente sera enviado antes del 31 de marzo del ano siguiente (Art. 381 ET).
        Generado el {new Date(data.generatedAt).toLocaleDateString('es-CO')}.
      </div>
    </div>
  );
}
